import React from 'react';
import { reportError } from '@/utils/errors';

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

interface ErrorBoundaryProps extends React.PropsWithChildren {
  fallback?: React.ReactNode;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    reportError(error, 'Ошибка при отображении страницы!');
    console.error('ErrorBoundary caught:', error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError && this.state.error) {
      if (this.props.fallback !== undefined) {
        return this.props.fallback;
      }

      return (
        <main className="fixed inset-0 z-50 flex min-h-screen items-center justify-center bg-slate-50 px-6 py-12">
          <section className="w-full max-w-xl rounded-3xl border border-slate-200 bg-white px-8 py-12 text-center shadow-xl shadow-slate-200/60 sm:px-12">
            <div
              className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-orange-50 text-orange-500"
              aria-hidden="true"
            >
              <svg viewBox="0 0 24 24" className="h-8 w-8" fill="none" stroke="currentColor">
                <path
                  d="M12 8v4m0 4h.01M10.3 3.6 2.4 17.3A2 2 0 0 0 4.1 20h15.8a2 2 0 0 0 1.7-2.7L13.7 3.6a2 2 0 0 0-3.4 0Z"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <p className="mt-6 text-sm font-semibold uppercase tracking-widest text-orange-500">
              Техническая ошибка
            </p>
            <h1 className="mt-2 text-3xl font-semibold text-slate-900">Что-то пошло не так</h1>
            <p className="mx-auto mt-4 max-w-md text-base leading-7 text-slate-600" role="alert">
              Мы уже работаем над исправлением. Попробуйте обновить страницу через несколько минут.
            </p>
            <button
              type="button"
              className="mt-8 rounded-xl bg-slate-900 px-6 py-3 font-medium text-white transition hover:bg-slate-700 focus:outline-none focus:ring-4 focus:ring-slate-200"
              onClick={this.handleRetry}
            >
              Обновить страницу
            </button>
          </section>
        </main>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
